import { alpha, createTheme, darken, getContrastRatio, lighten, Theme, ThemeOptions } from "@mui/material/styles";

export const DEFAULT_GATE_PRIMARY = '#0086CA';
export const DEFAULT_GATE_SECONDARY = '#404040';

const HEX_REGEX = /^#[0-9A-Fa-f]{6}$/;

function isValidHexColor(color?: string): color is string {
    return !!color && HEX_REGEX.test(color);
}

// Prah 3:1 odpovida vychozimu MUI contrastThreshold a WCAG AA pro velky/tucny text.
// Puvodnich 4.5 shodilo i firemni modrou #0086CA (pomer 3.99) na cerny popisek,
// takze tlacitko Prihlasit se melo cerny text misto bileho.
function getContrastText(color: string): string {
    return getContrastRatio(color, '#FFFFFF') >= 3 ? '#FFFFFF' : '#111111';
}

function buildBrandPalette(main: string) {
    return {
        main,
        light: lighten(main, 0.18),
        dark: darken(main, 0.14),
        contrastText: getContrastText(main),
    };
}

const DEFAULT_PRIMARY_PALETTE = buildBrandPalette(DEFAULT_GATE_PRIMARY);
const DEFAULT_SECONDARY_PALETTE = buildBrandPalette(DEFAULT_GATE_SECONDARY);
const WARNING_MAIN = '#FFB905';
const WARNING_DARK = '#9A5A00';
const WARNING_LIGHT = '#FFD76A';

const themeOptions: ThemeOptions = {

    palette: {
        mode: 'light',
        primary: DEFAULT_PRIMARY_PALETTE,
        warning: {
            main: WARNING_MAIN,
            light: WARNING_LIGHT,
            dark: WARNING_DARK,
            contrastText: '#1F1600',
        },
        secondary: DEFAULT_SECONDARY_PALETTE,
        background: {
            default: '#F5F5F5',
            paper: '#FFFFFF',
        },
        text: {
            primary: '#404040',
            secondary: '#777777',
        },
        error: {
            main: '#DD403A',
        },
        success: {
            main: '#85CB33',
        },
        divider: '#E6E6E6',
    },
    typography: {
        fontFamily: 'Mulish',
        h1: {
            fontFamily: 'Mulish',
        },
        h2: {
            fontFamily: 'Mulish',
        },
        h3: {
            fontFamily: 'Mulish',
        },
        h4: {
            fontFamily: 'Mulish',
        },
        h5: {
            fontFamily: 'Mulish',
        },
        h6: {
            fontFamily: 'Mulish',
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    fontWeight: 700,
                    textTransform: 'none',
                    transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
                },
            },
        },
    },
    shape: {
        borderRadius: 4,
    },
};
export default function CreateGateTheme(primaryColor?: string, secondaryColor?: string): Theme {
    const palette = themeOptions.palette ?? {};
    const resolvedPrimary = isValidHexColor(primaryColor) ? buildBrandPalette(primaryColor) : DEFAULT_PRIMARY_PALETTE;
    const resolvedSecondary = isValidHexColor(secondaryColor) ? buildBrandPalette(secondaryColor) : DEFAULT_SECONDARY_PALETTE;

    const overridden: ThemeOptions = {
        ...themeOptions,
        palette: {
            ...palette,
            primary: resolvedPrimary,
            secondary: resolvedSecondary,
        },
        components: {
            MuiDialog: {
                defaultProps: {
                    // Klik vedle dialogu (do backdropu) nesmi zadny dialog zavrit - uzivatel tim
                    // prichazel o rozdelanou praci. MUI si pro backdrop klik vesi vlastni onClick na
                    // root slot dialogu (vola onClose(e, 'backdropClick')) a props z slotProps.root
                    // se spreaduji az za nej, takze tohle ten handler globalne prebije.
                    // Zavrit dialog jde dal krizkem / tlacitkem v akcich a klavesou Escape.
                    // Stejne nastaveni ma i validator (CreateValidatorTheme.ts).
                    slotProps: {
                        root: {
                            onClick: () => { /* backdrop click zamerne nic nedela */ },
                        },
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        fontWeight: 700,
                        textTransform: 'none',
                        transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
                    },
                    containedPrimary: {
                        backgroundColor: resolvedPrimary.main,
                        color: resolvedPrimary.contrastText,
                        boxShadow: `0 8px 18px ${alpha(resolvedPrimary.main, 0.22)}`,
                        '&:hover': {
                            backgroundColor: alpha(resolvedPrimary.main, 0.92),
                            boxShadow: `0 10px 22px ${alpha(resolvedPrimary.main, 0.28)}`,
                        },
                    },
                    containedSecondary: {
                        backgroundColor: resolvedSecondary.main,
                        color: resolvedSecondary.contrastText,
                        boxShadow: `0 8px 18px ${alpha(resolvedSecondary.main, 0.18)}`,
                        '&:hover': {
                            backgroundColor: alpha(resolvedSecondary.main, 0.92),
                            boxShadow: `0 10px 22px ${alpha(resolvedSecondary.main, 0.24)}`,
                        },
                    },
                    textPrimary: {
                        '&:hover': {
                            backgroundColor: alpha(resolvedPrimary.main, 0.08),
                        },
                    },
                    textSecondary: {
                        '&:hover': {
                            backgroundColor: alpha(resolvedSecondary.main, 0.08),
                        },
                    },
                    outlinedPrimary: {
                        '&:hover': {
                            backgroundColor: alpha(resolvedPrimary.main, 0.06),
                            borderColor: alpha(resolvedPrimary.main, 0.36),
                        },
                    },
                    outlinedSecondary: {
                        '&:hover': {
                            backgroundColor: alpha(resolvedSecondary.main, 0.06),
                            borderColor: alpha(resolvedSecondary.main, 0.32),
                        },
                    },
                    textWarning: {
                        color: WARNING_DARK,
                        '&:hover': {
                            backgroundColor: alpha(WARNING_DARK, 0.08),
                        },
                    },
                    outlinedWarning: {
                        color: WARNING_DARK,
                        borderColor: alpha(WARNING_DARK, 0.32),
                        '&:hover': {
                            backgroundColor: alpha(WARNING_DARK, 0.06),
                            borderColor: alpha(WARNING_DARK, 0.5),
                        },
                    },
                    containedWarning: {
                        backgroundColor: WARNING_MAIN,
                        color: '#1F1600',
                        boxShadow: `0 8px 18px ${alpha(WARNING_DARK, 0.18)}`,
                        '&:hover': {
                            backgroundColor: alpha(WARNING_MAIN, 0.9),
                            boxShadow: `0 10px 22px ${alpha(WARNING_DARK, 0.24)}`,
                        },
                    },
                },
            },
        },
    };

    return createTheme(overridden);
}